/// <amd-module name="Test/TestResultsGridToolbar/Components/TestResultsGridToolbar" />
import * as React from "react";
import {
    ColumnIndices,
    ContributionSource,
    FeatureFlagConstants,
    FilterByFields,
    IViewContextData,
    TestResultFlakyState,
    TestResultsGroupPivots,
    ViewContext,
    ViewContextStatus,
} from "Test/common-util/Common";
import { IResultsToolbarContributionContext } from "Test/common-util/Contracts/ContributedExtension.Contracts";
import { FilterState } from "Test/common-util/Contracts/Filters";
import { IContributedToolbarProps, ITestResultTreeData } from "Test/common-util/Contracts/ViewContracts";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { ITestFlakyDialogProps, TestFlakyDialog } from "Test/TestFlaky/Components/TestFlakyDialog";
import { IAssociateToTestCaseDialogProps, AssociateToTestCaseDialog } from "Test/AssociateToTestCase/Components/AssociateToTestCaseDialog";
import { FilterActionHub } from "Test/TestResultsFilter/Actions/FilterActionHub";
import { TestResultsFilter } from "Test/TestResultsFilter/TestResultsFilter";
import { ITestResultsFilterField, TestResultsFilterFieldType } from "Test/TestResultsFilter/Types";
import { TestResultsLeftViewActionHub } from "Test/TestResultsGrid/Actions/TestResultsGridActionHub";
import * as GridResources from "Test/TestResultsGrid/Resources";
import { TestResultsStore } from "Test/TestResultsGrid/Store/TestResultsTreeStore";
import { FilterActionsCreator } from "Test/TestResultsGridToolbar/Actions/FilterActionCreator";
import { TestResultsToolbarActionCreator } from "Test/TestResultsGridToolbar/Actions/TestResultsToolbarActionCreator";
import { TestResultsToolbarActionsHub } from "Test/TestResultsGridToolbar/Actions/TestResultsToolbarActionshub";
import { FlakyState } from "Test/TestResultsGridToolbar/GridToolBar";
import * as Resources from "Test/TestResultsGridToolbar/Resources";
import { OutcomeFilterValueProvider } from "Test/TestResultsGridToolbar/Stores/FilterOutcomeProvider";
import { FilterStore } from "Test/TestResultsGridToolbar/Stores/FilterStore";
import { IToolBarState, TestResultsToolbarStore } from "Test/TestResultsGridToolbar/Stores/TestResultsToolbarStore";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { contributionIconToIconProps } from "VSS/Features/Frame/ContributedIconUtil";
import { IVssContributableActionService } from "VSS/Features/Frame/IVssContributableActionService";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { IVssPageContext } from "VSS/Platform/Context";
import { IContributedCommand, IContributedService, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import {
    Components,
    IVssComponentContext,
    IVssComponentProperties,
    IVssLayoutManager,
    VssComponent,
} from "VSS/Platform/Layout";
import { IVssUserClaimsService, UserClaims } from "VSS/Platform/UserClaims";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon, IIconProps } from "VSSUI/Icon";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";

export interface IToolbarOptions extends IVssComponentProperties {
    context: IViewContextData;
    treeStore: TestResultsStore;
    isNoConfigRuns?: boolean;
    onGroupByChange: (groupByValue: TestResultsGroupPivots) => void;
    onFilterChange: (filter: FilterState) => void;
    onColumnsChange: (columns: string[]) => void;
    onViewMoreTestsClicked: () => void;
    onFlakyStateChange: () => void;
    parentActionHub: TestResultsLeftViewActionHub;
}

export enum TestResultFieldIds {
    Duration = 400,
    FailingRelease = 401,
    FailingSince = 402,
    Owner = 403,
    DateStarted = 404,
    DateCompleted = 405,
    EnvironmentName = 406,
    FailingBuild = 407,
    Tags = 408
}

export class TestResultsGridToolbar extends VssComponent<IToolbarOptions, IToolBarState, IVssComponentContext> {
    private _store: TestResultsToolbarStore;
    private flakyState: FlakyState;
    private _flakyTestResult: ITestResultTreeData;
    private _actionHub: TestResultsToolbarActionsHub;
    private _pageContext: IVssPageContext;
    private _testResultsToolbarActionCreator: TestResultsToolbarActionCreator;
    private _filterStore: FilterStore;
    private _filterActionCreator: FilterActionsCreator;
    private _filterActionHub: FilterActionHub;
    private _testInAzurePipelineFwLink: string = "https://go.microsoft.com/fwlink/?linkid=2056113";
    private _tagsForTestRunFF: string = "TestManagement.Server.TagsInTestRun";
    private _unifiedPipelineStageFilter: string = "WebAccess.TestManagement.UnifiedPipelineStageFilter";
    private _defaultJobName: string = "__default";
    private _refToolBarContainer: any;
    private _toolBarContainerSize = new ObservableValue<number | undefined>(undefined);

    public constructor(props: IToolbarOptions, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        this._actionHub = new TestResultsToolbarActionsHub();
        this._store = new TestResultsToolbarStore(
            this._pageContext,
            this._actionHub,
            this.props.parentActionHub,
            this.props.treeStore,
            this.props.context
        );
        this.state = {
            filterState: this.props.treeStore.getState().filterState,
            groupBy: this.props.treeStore.getState().groupByField,
            isFilterApplied: this._store.getState().isFilterApplied,
            isFilterBarEnabled: this._store.getState().isFilterBarEnabled,
            isReloadButtonEnabled: this._store.getState().isReloadButtonEnabled,
            isManualMarkUnMarkFlakyEnabled: this._store.getState().isManualMarkUnMarkFlakyEnabled
        };
        this.flakyState = FlakyState.Flaky;
        this._flakyTestResult = {} as ITestResultTreeData;
        this._testResultsToolbarActionCreator = new TestResultsToolbarActionCreator(this._actionHub, this._pageContext);
    }

    public componentWillMount(): void {
        this._initializeFilterStoreObject();
        this._store.addChangedListener(this._handleStoreChange);
    }

    public componentDidMount() {
        if (isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.ShowTestProjectSettings, false)) {
            this._testResultsToolbarActionCreator.getTestResultsSettings();
        }

        this._updateCurrentWidthOfToolBarContainer();
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._handleStoreChange);
    }

    // Handle the scenario when status change from inprogress to complete
    public componentDidUpdate(prevProps: IToolbarOptions) {
        if (prevProps.context.status === ViewContextStatus.InProgress && this.props.context.status === ViewContextStatus.Completed) {
            this._filterActionCreator.clearFilterValues();
        }

        this._updateCurrentWidthOfToolBarContainer();
    }

    public render(): JSX.Element {
        return (
            <div ref={(container: HTMLDivElement) => (this._refToolBarContainer = container)}>
                <Observer width={this._toolBarContainerSize}>
                    {(sizeProps: {width: number | undefined}) => {
                        if(sizeProps.width === undefined) {
                            return null;
                        }
                        else {
                            return (
                                <>
                                    {this.props.isNoConfigRuns && this.props.treeStore.getState().failedAbortedCount > 0 && (
                                        <div className="no-config-runs-info-bar flex-row flex-center">
                                            <Icon
                                                iconName="Info"
                                                className="no-config-info-icon"
                                            ></Icon>
                                            <div className="no-config-runs-info-message">
                                                {Resources.NoConfigInfoMessage}
                                                <FPSLinkComponent
                                                    className="test-in-azure-pipeline-link"
                                                    href={this._testInAzurePipelineFwLink}
                                                    telemetrySource={TelemetryConstants.featureTestTab_TestInAzurePipelineFwLinkClicked}
                                                >
                                                    {Resources.LearnMoreText}
                                                </FPSLinkComponent>
                                            </div>
                                        </div>
                                    )}
                                    {this._TestTabHeaderCommandBar(sizeProps.width)}
        
                                    {!this.props.isNoConfigRuns && this.state.isFilterBarEnabled && (
                                        <TestResultsFilter
                                            fields={this._getFilterFieldValues()}
                                            store={this._filterStore}
                                            initialFilterState={this.state.filterState}
                                            actionCreator={this._filterActionCreator}
                                            filterUpdatedCallback={(filter: FilterState) => {
                                                this._actionHub.filterChanged.invoke(filter);
                                                this.props.onFilterChange(filter);
                                            }}
                                        />
                                    )}
                                </>
                                );
                            }
                        }
                    }
                </Observer>
            </div>
            );
    }

    private _updateCurrentWidthOfToolBarContainer() {
        if(!!this._refToolBarContainer){
            this._toolBarContainerSize.value = this._refToolBarContainer.clientWidth;
        }
    }

    private _TestTabHeaderCommandBar = (width: number) => {

        const commandBarFarItems: IHeaderCommandBarItem[] = [];
        let commandBarItems: IHeaderCommandBarItem[] = [];

        if (!this.props.isNoConfigRuns) {
            commandBarItems = this._getContributedMenuItems();

            if (this._shouldShowMarkUnMark() && this.isMember()) {
                commandBarItems.push(this._getMarkUnMarkFlakyOption());
            }

            //Associate to TestCase in TCM
            if (this._shouldShowAssociateToTestCase() && this.isMember()) {
                commandBarItems.push(this._getAssociateToTestCaseOption());
            }

            if (this.isArtifactInProgress()) {
                commandBarItems.push(this._getInProgressButton());
            }

            while (width <= this._minWidthRequiredToRenderAllItemOfCommandBar && commandBarItems.length > 4) {
                let poppedItem = commandBarItems.pop();
                if (poppedItem != undefined) {
                    poppedItem.important = false;
                    commandBarFarItems.push(poppedItem);
                }
            }


            commandBarFarItems.push(this._getGroupByMenuItem(width));
            commandBarFarItems.push(this._getColumnOptionMenuItem(width));
            commandBarFarItems.push(this._getFilterToggleButton(width));
        }
        return (!this.props.isNoConfigRuns && (
            <div className="flex-row">
                <HeaderCommandBar className={"test-result-command-bar"} items={commandBarItems} ></HeaderCommandBar>
                <HeaderCommandBar className={"test-result-command-bar-far"} items={commandBarFarItems} ></HeaderCommandBar>
            </div>

        ));



    };
    private _shouldShowMarkUnMark(): boolean {
        if (isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.ShowTestProjectSettings, false)) {
            return this.state.isManualMarkUnMarkFlakyEnabled;
        }
        if (isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.EnableTestFlakiness, false)) {
            return true;
        }
        return false;
    }

    private _shouldShowAssociateToTestCase(): boolean {
        if (isFeatureFlagEnabled(this._pageContext, FeatureFlagConstants.AssociateToTestCase, false)) {
            return true;
        }
        return false;
    }

    private _getInProgressButton(): IHeaderCommandBarItem {
        return {
            id: "inprogress-viewmoreresults-button",
            text: Resources.ViewMoreTests,
            iconProps: this._getIconPropsForInProgressButton(),
            ariaLabel: Resources.ViewMoreTests,
            disabled: !this.state.isReloadButtonEnabled,
            onActivate: this._viewMoreResults,
            className: "inprogress-button",
            important: true,
            subtle: true
        };
    }

    private _getAssociateToTestCaseOption(): IHeaderCommandBarItem {
        const selectedItems = this.props.treeStore.getState().selectedTreeItems;
        const enabled = selectedItems && selectedItems.length == 1 && !selectedItems[0].isSubResultRow && selectedItems[0].isTestCaseRow;
        return {
            id: "associate-to-testcase-button",
            text: Resources.AssociateToTestCase,
            iconProps: this._getIconPropsForAssociateToTestCase(),
            disabled: !enabled,
            important: true,
            subtle: true,
            onActivate: (): void => {
                if (selectedItems && selectedItems[0]) {
                    this.renderAssociateToTestCaseDialog(selectedItems[0]);
                }
            }
        };
    }

    private renderAssociateToTestCaseDialog(treeItem: ITestResultTreeData) {
        if (!treeItem.runId || !treeItem.resultId) {
            return;
        }

        const associateToTestCaseDialogProps: IAssociateToTestCaseDialogProps = {
            testRunId: treeItem.runId,
            testResultId: treeItem.resultId,
            associateToTestCaseResult: treeItem
        };

        const layoutManager = this._pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(() => {
            return React.createElement(AssociateToTestCaseDialog, associateToTestCaseDialogProps);
        });
    }

    private _getMarkUnMarkFlakyOption(): IHeaderCommandBarItem {
        const selectedItems = this._getSelectedTestResult();
        const enabled = selectedItems && selectedItems.length == 1 && !selectedItems[0].isSubResultRow && selectedItems[0].isTestCaseRow;
        return {
            id: "flaky-button",
            text: this.flakyState === FlakyState.Flaky ? Resources.FlakyText : Resources.UnFlakyTest,
            iconProps: this._getIconPropsForTestFlakyButton(),
            disabled: !enabled,
            important: true,
            subtle: true,
            onActivate: (): void => {
                if (selectedItems && selectedItems[0]) {
                    this.renderFlakinessDialog(selectedItems[0]);
                }
            }
        };
    }

    private renderFlakinessDialog(treeItem: ITestResultTreeData) {
        if (!treeItem.runId || !treeItem.resultId) {
            return;
        }
        this._flakyTestResult = treeItem;

        const flakyDialogProps: ITestFlakyDialogProps = {
            testRunId: treeItem.runId,
            testResultId: treeItem.resultId,
            isTestResultFlaky: !(this.flakyState === FlakyState.Flaky),
            flakyTestResult: treeItem,
            onFlakyStateChange: this.props.onFlakyStateChange,
            testCaseRefId: treeItem.testCaseRefId
        };

        const layoutManager = this._pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(() => {
            return React.createElement(TestFlakyDialog, flakyDialogProps);
        });
    }

    private _getContributedMenuItems(): IHeaderCommandBarItem[] {
        const commandBarItems: IHeaderCommandBarItem[] = [];
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        const children = contributionService.getContributionChildren("ms.vss-test-web.test-results-actions-menu", "ms.vss-web.action");

        if (children) {
            for (const childId of children) {
                // look up the contribution for the child
                const menuContribution: IContributedToolbarProps | undefined = contributionService.getContribution<IContributedToolbarProps>(childId);

                if (menuContribution) {
                    commandBarItems.push(this._createMenuItem(menuContribution, childId));
                }
            }
        }

        return commandBarItems;
    }

    private _createMenuItem(menuContribution: IContributedToolbarProps, menuItemId: string): IHeaderCommandBarItem {
        const selectedItems = this._getNonGroupSelectedTestResult();
        const menuItem: IHeaderCommandBarItem = {
            id: menuContribution.key ? menuContribution.key : menuItemId,
            iconProps: menuContribution.iconProps || contributionIconToIconProps(this._pageContext, menuItemId, menuContribution.icon),
            text: menuContribution.text,
            disabled: !selectedItems || selectedItems.length == 0,
            important: true,
            subtle: true,
            onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
                if (menuContribution.command) {
                    const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
                    contributionService.executeCommandEx(
                        menuItem.id,
                        menuContribution.command as IContributedCommand,
                        menuItem.id,
                        this._getNonGroupSelectedTestResult(),
                        this.props.context
                    );
                } else {
                    this._executeThirdPartyExtension(menuContribution, menuItem);
                }
            }
        } as IHeaderCommandBarItem;

        if (menuContribution.subMenu) {
            const subMenuItems: IMenuItem[] = [];
            for (const subMenuItem of menuContribution.subMenu) {
                subMenuItems.push({
                    id: subMenuItem.key ? subMenuItem.key : subMenuItem.text,
                    text: subMenuItem.text,
                    iconProps: subMenuItem.iconProps
                } as IMenuItem);
            }

            menuItem.subMenuProps = {
                items: subMenuItems,
                id: menuItem.id,
                onActivate: menuContribution.subMenuCommand
                    ? (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
                        contributionService.executeCommandEx(
                            menuItemId,
                            menuContribution.subMenuCommand as IContributedCommand,
                            menuItem.id,
                            this._getNonGroupSelectedTestResult(),
                            this.props.context
                        );
                    }
                    : undefined
            };
        }

        return menuItem;
    }

    private _executeThirdPartyExtension = (menuContribution: IContributedToolbarProps, item: IMenuItem): void => {
        // Sample 3rd party extensions for Tests tab: https://github.com/paraggangil/azure-devops-extensions
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributedService: IContributedService = {
            serviceName: menuContribution.registeredObjectId || menuContribution.key,
            uri: menuContribution.uri,
            configuration: this.props.context
        };

        contributionService.getServiceEx<IVssContributableActionService>(item.id, contributedService, true).then(contributedActionService => {
            if (contributedActionService && contributedActionService.execute) {
                contributedActionService.execute(this._getContextForExtension());
            }
        });
    };

    private _getContextForExtension(): IResultsToolbarContributionContext {
        const resultsToolbarContributionContext: IResultsToolbarContributionContext = {
            viewContext: this.props.context,
            selectedTestResults: []
        };
        const selectedTestResults: ITestResultTreeData[] = this._getNonGroupSelectedTestResult() as ITestResultTreeData[];
        selectedTestResults.forEach((testResult: ITestResultTreeData) => {
            resultsToolbarContributionContext.selectedTestResults.push({
                resultId: testResult.resultId as number,
                runId: testResult.runId
            });
        });

        return resultsToolbarContributionContext;
    }

    private _getSelectedTestResult(): ITestResultTreeData[] | undefined {
        const selectedTreeDatas: ITestResultTreeData[] | undefined = this.props.treeStore.getState().selectedTreeItems;
        if (selectedTreeDatas && selectedTreeDatas.length > 0) {
            selectedTreeDatas.forEach(result => {

                // Mark Flaky is allowed for single test result.
                if ((result.isUnreliable && result.flakyState !== TestResultFlakyState.Unmarked) ||
                    result.flakyState === TestResultFlakyState.Marked) {
                    this.flakyState = FlakyState.UnFlaky;
                } else {
                    this.flakyState = FlakyState.Flaky;
                }
            });
        }
        return selectedTreeDatas;
    }

    private _getNonGroupSelectedTestResult(): ITestResultTreeData[] | undefined {
        const selectedTreeDatas: ITestResultTreeData[] | undefined = this.props.treeStore.getState().selectedTreeItems;
        const selectedNonGroupTreeData: ITestResultTreeData[] = [];
        if (selectedTreeDatas && selectedTreeDatas.length > 0) {
            selectedTreeDatas.forEach(result => {
                if (result.isTestCaseRow) {
                    selectedNonGroupTreeData.push(result);
                }
            });
        }
        return selectedNonGroupTreeData;
    }

    private _getNameOfGroupBySelectedKey(): string | undefined {
        const foundItem = this._getGroupByOptions().find(item => item.id === this.state.groupBy);

        return foundItem ? foundItem.text : "";
    }

    private _onClickOfGroupByMenuItem = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        const currentState = menuItem.id.toString();
        const oldState = this.state.groupBy;
        if (currentState === oldState) {
            return;
        }

        this._actionHub.onGroupByChanged.invoke(menuItem.id);
        this.props.onGroupByChange(menuItem.id as TestResultsGroupPivots);
        Telemetry.publishEvent(this.context.pageContext, this.props.context, TelemetryConstants.featureTestTab_GroupByClicked, { currentState });
    };

    private _viewMoreResults = () => {
        this._actionHub.updateReloadButton.invoke(false);
        this.props.onViewMoreTestsClicked();
        Telemetry.publishEvent(this.context.pageContext, this.props.context, TelemetryConstants.featureTestTab_InProgressViewRefreshed, { count: 1 });
    };

    private _toggleShowFilterBarButton = () => {
        this._actionHub.onToggleFilter.invoke(undefined);
    };

    private _getColumnWidth = (fieldRefName: string): number => {
        return 80;
    };

    private _onColumnOptionsClick = () => {
        const displayColumns: any[] = [];
        this.getAllAvailableColumns(this.props.context.viewContext).map(col => {
            if (this.props.treeStore.getState().columnToRender.indexOf(col.key) > -1) {
                displayColumns.push({
                    fieldRefName: col.key,
                    width: col.width
                });
            }
        });

        const pageService = this._pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        if (pageData && pageData.project) {
            const projectId = pageData.project.id;

            const panelProps = {
                getFieldWidth: this._getColumnWidth,
                getAvailableFields: this._getAvailableColumnsAsFieldDefinitions,
                displayColumns,
                allowSort: false,
                project: projectId
            };

            const layoutManager = this._pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout((onDismiss: () => void) => {
                return React.createElement(
                    LegacyComponent,
                    {
                        modules: ["WorkItemTracking/Scripts/Queries/Components/ColumnOptions/ColumnOptionsPanel"],
                        wrappedType: "columnOptionsPanel",
                        ...panelProps,
                        onOkClick: (newColumns: any, dataChanged?: boolean) => {
                            onDismiss();
                            if (dataChanged) {
                                this.props.onColumnsChange(newColumns.display.map((col: any) => col.name));
                                Telemetry.publishEvent(
                                    this.context.pageContext,
                                    this.props.context,
                                    TelemetryConstants.featureTestTab_ColumnsChanged,
                                    {
                                        NumberOfColumns: newColumns.display.length,
                                        ColumnsAdded: newColumns.added
                                    }
                                );
                            }
                        },
                        onDismiss
                    },
                    null
                );
            });
        }
    };

    private _getAvailableColumnsAsFieldDefinitions = (): Promise<any[]> => {
        const allFields: any[] = [];

        this.getAllAvailableColumns(this.props.context.viewContext).map(af =>
            allFields.push({
                id: af.id,
                name: af.name,
                referenceName: af.key,
                canSortBy: (): boolean => {
                    return false;
                },
                isQueryable: (): boolean => {
                    return true;
                }
            })
        );

        return new Promise((resolve, reject) => {
            resolve(allFields);
        });
    };

    private _getColumnOptionMenuItem(width: number): IHeaderCommandBarItem {
        return {
            id: "column-options-button",
            text: Resources.ColumnOptionsTitle,
            ariaLabel: Resources.ColumnOptionsTitle,
            iconProps: { iconName: "Repair" },
            onActivate: this._onColumnOptionsClick,
            important: width >= this._minWidthRequiredToRenderAllItemOfCommandBar,
            subtle: true,
        };
    }

    private _getGroupByMenuItem(width: number): IHeaderCommandBarItem {
        const selectedName = this._getNameOfGroupBySelectedKey();
        const ariaLabel = format(Resources.GroupByFormat, selectedName);
        return {
            id: "group-by-result",
            text: selectedName,
            disabled: this.isArtifactInProgress(),
            ariaLabel: ariaLabel,
            iconProps: { iconName: "RowsGroup" },
            subMenuProps: {
                id: "group-by-submenu",
                items: this._getGroupByOptions(),
                onActivate: this._onClickOfGroupByMenuItem,

            },
            // first we hide ColumnOption menu item if not enough space to render.
            important: width >= (this._minWidthRequiredToRenderAllItemOfCommandBar - this._widthOfColumOptionMenuItem),
            subtle: true,
        };
    }

    private _getFilterToggleButton(width: number): IHeaderCommandBarItem {
        return {
            id: "toggle-filter-button",
            iconProps: this._getIconPropsForFilterButton(),
            ariaLabel: this.state.isFilterBarEnabled ? Resources.HideFilterBarTitle : Resources.ShowFilterBarTitle,
            onActivate: this._toggleShowFilterBarButton,
            // first we hide columnOption and then groupBy menu item if not enough space to render.
            important: width >= (this._minWidthRequiredToRenderAllItemOfCommandBar - this._widthOfGroupByMenuItem - this._widthOfColumOptionMenuItem),
            subtle: true,
        };
    }

    private _getIconPropsForFilterButton(): IIconProps {
        if (this.state.isFilterBarEnabled) {
            return { iconName: "FilterSolid" };
        }

        return { iconName: "Filter" };
    }

    private _getIconPropsForInProgressButton(): IIconProps {
        return { iconName: "Refresh" };
    }

    private _getIconPropsForTestFlakyButton(): IIconProps {
        return { iconName: "Snowflake" };
    }

    private _getIconPropsForAssociateToTestCase(): IIconProps {
        return { iconName: "TestPlan" };
    }

    private isArtifactInProgress(): boolean {
        return this.props.context.status === ViewContextStatus.InProgress;
    }
    private isMember(): boolean {
        return this._pageContext.getService<IVssUserClaimsService>("IVssUserClaimsService").hasClaim(UserClaims.Member);
    }

    private _getGroupByOptions(): IMenuItem[] {
        const groupByOptions: IMenuItem[] = [];
        groupByOptions.push({
            id: TestResultsGroupPivots.Group_By_Test_Run,
            text: Resources.TestRunText
        });
        groupByOptions.push({
            id: TestResultsGroupPivots.Group_By_Container,
            text: Resources.TestFileText
        });
        groupByOptions.push({
            id: TestResultsGroupPivots.Group_By_Priority,
            text: Resources.PriorityText
        });

        groupByOptions.push({
            id: TestResultsGroupPivots.Group_By_Owner,
            text: Resources.Owner
        });
        groupByOptions.push({
            id: TestResultsGroupPivots.Group_By_None,
            text: Resources.NoneText
        });

        return groupByOptions;
    }

    private _getFilterFieldValues(): ITestResultsFilterField[] {
        const filterField: ITestResultsFilterField[] = [
            {
                displayType: TestResultsFilterFieldType.Text,
                fieldName: FilterByFields.TestCaseName,
                placeholder: Resources.FilterByTestName
            }
        ];

        // to be added only if it is unified pipeline with stages
        if (isFeatureFlagEnabled(this._pageContext, this._unifiedPipelineStageFilter, false)) {
            if (this.props.context.contributionSource === ContributionSource.Pipeline) {
                const stages = this.props.context.data.mainData.stages;
                if (stages.length >= 1 && stages[0].name !== this._defaultJobName) {
                    filterField.push({
                        displayType: TestResultsFilterFieldType.CheckboxList,
                        fieldName: FilterByFields.Stages,
                        placeholder: Resources.FilterStagesText,
                        noItemsText: Resources.FilterNoStages
                    });
                }
            }
        }

        if (
            isFeatureFlagEnabled(this._pageContext, this._tagsForTestRunFF, false) &&
            this.state.groupBy === TestResultsGroupPivots.Group_By_Test_Run
        ) {
            filterField.push({
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: FilterByFields.Tags,
                placeholder: Resources.FilterTagsText,
                noItemsText: Resources.FilterNoTags
            });
        }

        filterField.push(
            {
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: FilterByFields.Container,
                placeholder: Resources.TestFileText,
                noItemsText: Resources.FilterNoTestFile
            },
            {
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: FilterByFields.Owner,
                placeholder: Resources.Owner,
                noItemsText: Resources.FilterNoOwner
            },
            {
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: FilterByFields.Outcome,
                placeholder: Resources.Outcome,
                valueProvider: new OutcomeFilterValueProvider()
            }
        );

        return filterField;
    }

    private _initializeFilterStoreObject() {
        if (!this._filterActionHub) {
            this._filterActionHub = new FilterActionHub();

            this._filterStore = new FilterStore(this._filterActionHub, this.props.parentActionHub, this._pageContext);
            this._filterActionCreator = new FilterActionsCreator(this._filterActionHub, this.props.context, this._pageContext);
        }
    }

    private _handleStoreChange = (): void => {
        this.setState({
            filterState: this._store.getState().filterState,
            groupBy: this._store.getState().groupBy,
            isFilterApplied: this._store.getState().isFilterApplied,
            isFilterBarEnabled: this._store.getState().isFilterBarEnabled,
            isReloadButtonEnabled: this._store.getState().isReloadButtonEnabled,
            isManualMarkUnMarkFlakyEnabled: this._store.getState().isManualMarkUnMarkFlakyEnabled
        });
    };

    private getAllAvailableColumns(viewContext: ViewContext): any[] {
        const columns = [];
        columns.push({
            id: TestResultFieldIds.Duration,
            key: ColumnIndices.Duration,
            name: GridResources.QueryColumnNameDuration,
            width: 80
        });
        if (viewContext === ViewContext.Build) {
            columns.push({
                id: TestResultFieldIds.FailingBuild,
                key: ColumnIndices.FailingBuild,
                name: GridResources.ResultGridHeader_FailingBuild,
                width: 100
            });
        } else if (viewContext === ViewContext.Release) {
            columns.push({
                id: TestResultFieldIds.FailingRelease,
                key: ColumnIndices.FailingRelease,
                name: GridResources.ResultGridHeader_FailingRelease,
                width: 100
            });
        }
        columns.push({
            id: TestResultFieldIds.FailingSince,
            key: ColumnIndices.FailingSince,
            name: GridResources.ResultGridHeader_FailingSince,
            width: 90
        });
        columns.push({
            id: TestResultFieldIds.Owner,
            key: ColumnIndices.Owner,
            name: GridResources.ResultGridHeader_Owner,
            width: 120
        });
        columns.push({
            id: TestResultFieldIds.DateStarted,
            key: ColumnIndices.DateStarted,
            name: GridResources.ResultGridHeader_DateStarted,
            width: 150
        });
        columns.push({
            id: TestResultFieldIds.DateCompleted,
            key: ColumnIndices.DateCompleted,
            name: GridResources.ResultGridHeader_DateCompleted,
            width: 150
        });

        if (isFeatureFlagEnabled(this._pageContext, this._tagsForTestRunFF, false)) {
            columns.push({
                id: TestResultFieldIds.Tags,
                key: ColumnIndices.Tags,
                name: GridResources.ResultGridHeader_Tags,
                width: 150
            });
        }

        return columns;
    }

    private _minWidthRequiredToRenderAllItemOfCommandBar = 665; // all include (bug, link, flaky, groupBy, ColumnOption and Filter)
    private _widthOfGroupByMenuItem = 100;   // Approximate width of GroupBY menu item
    private _widthOfColumOptionMenuItem = 130; // Approximate width of ColumnOption menu item.
}

Components.add("test-results-grid-toolbar", TestResultsGridToolbar);
